// De uitsplitsing van een materialen-benchmark naar contextkenmerken, als
// secties voor de detailpagina: één per vraag, in de volgorde van de
// vragenlijst, met de antwoorden in de volgorde van hun opties.
//
// Rekent niets uit. De niveaus komen van de server
// (functions/materialsHelpers/buildMaterialContextSplit.js); hier komen alleen
// de labels en de volgorde bij, uit de vragenlijst die de TOC ook gebruikt.
//
// AI-CONTEXT: See docs/features/sector-context-questions.md

// Onder dit aantal organisaties is een groep te klein om iets over te zeggen:
// één of twee organisaties zijn een anekdote, geen vergelijking. De waarde
// blijft staan (beheerpagina, besluit user 2026-10-12), met een markering.
export const MIN_COMPANIES_TO_COMPARE = 3

const NIET_INGEVULD = 'Niet ingevuld'

/**
 * @typedef {object} MaterialContextRow
 * @property {string} key
 * @property {string} label
 * @property {number} companyCount
 * @property {number} householdCount
 * @property {number|null} benchmarkLevel
 * @property {boolean} tooFew - Minder dan MIN_COMPANIES_TO_COMPARE organisaties
 */

/**
 * @typedef {object} MaterialContextSection
 * @property {string} id - Vraag-id
 * @property {string} label - De vraag zoals de organisatie hem leest
 * @property {MaterialContextRow[]} rows
 */

/** @param {string} key @param {string} label @param {object} groep @returns {MaterialContextRow} */
const rij = (key, label, groep) => ({
    key,
    label,
    companyCount: groep.companyCount,
    householdCount: groep.householdCount,
    benchmarkLevel: groep.benchmarkLevel,
    tooFew: groep.companyCount < MIN_COMPANIES_TO_COMPARE,
})

/**
 * @param {object|null} split - `contextSplit` uit materialOutputBenchmark
 * @param {Array<{questions: Array<{id: string, label: string, options: Array<{value: string, label: string}>}>}>} groups - sectorContextQuestions.<sector>.groups
 * @returns {MaterialContextSection[]}
 */
const buildMaterialContextRows = (split, groups) => {
    if (!split || !Array.isArray(groups)) return []
    const perVraag = split.questions || {}

    return groups.flatMap((group) => group.questions).map((vraag) => {
        const data = perVraag[vraag.id]
        // Niemand beantwoordde de vraag: dan is de hele basis de groep zonder
        // antwoord, met de waarde van de basis.
        if (!data) {
            return { id: vraag.id, label: vraag.label, rows: [rij('niet-ingevuld', NIET_INGEVULD, split)] }
        }

        const opties = vraag.options.map((o) => o.value)
        const bekend = vraag.options
            .map((optie) => {
                const groep = data.answers.find((a) => a.value === optie.value)
                return groep ? rij(optie.value, optie.label, groep) : null
            })
            .filter(Boolean)
        // Een opgeslagen antwoord dat geen optie (meer) is weglaten zou
        // organisaties laten verdwijnen; dan staat de rauwe waarde er.
        const onbekend = data.answers
            .filter((a) => !opties.includes(a.value))
            .map((a) => rij(a.value, a.value, a))
        const zonder = data.notAnswered && data.notAnswered.companyCount > 0
            ? [rij('niet-ingevuld', NIET_INGEVULD, data.notAnswered)]
            : []

        return { id: vraag.id, label: vraag.label, rows: [...bekend, ...onbekend, ...zonder] }
    })
}

export default buildMaterialContextRows
